import { useState, useEffect } from 'react';
import { Button, Input } from '@components/ui';
import {
  MapPinIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  BellIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import type { InvoiceWizardData } from '../../types/invoiceWizard.types';
import type { EmailReceiver } from '../../types/invoice.types';
import { CustomerSearchModal } from './CustomerSearchModal';
import { isValidBranchOfficeCode, resolveCorrectionBranchOfficeCode } from '../../utils/branchOfficeCode';
import { AddressModal } from './AddressModal';
import { BranchOfficeFormModal } from '@features/companies/components/BranchOfficeFormModal';
import { useCompanies } from '@features/companies/hooks/useCompanies';
import { useBranchOffices } from '@features/companies/hooks/useBranchOffices';
import { useCountries } from '@features/customers/hooks/useGeolocation';
import { displayCustomerName, displayCustomerTaxId } from '@features/customers/utils/customerDisplay';
import { branchOfficeService } from '@features/branchOffice/services/branchOfficeService';
import { companyService } from '@features/company/services/companyService';

interface Step1CustomerSelectionProps {
  wizardData: InvoiceWizardData;
  onUpdate: (updates: Partial<InvoiceWizardData>) => void;
  onNext: () => void;
}

const RECEIVER_TYPES = [
  { value: '01', label: 'Contribuyente' },
  { value: '02', label: 'Consumidor Final' },
  { value: '03', label: 'Gobierno' },
  { value: '04', label: 'Extranjero' },
];

// Tipo de Documento según tipo de receptor DGI:
// - Contribuyente (01) / Gobierno (03): Solo RUC (gRucRec obligatorio)
// - Consumidor Final (02): RUC o Cédula (ambos van al XML en dRuc si el cliente lo solicita, DGI B4022)
// - Extranjero (04): No se muestra selector (usa gIdExt)
const DOCUMENT_TYPES_BY_RECEIVER: Record<string, Array<{ value: string; label: string }>> = {
  '01': [ // Contribuyente: solo RUC
    { value: '01', label: 'RUC - Registro Único del Contribuyente' },
  ],
  '02': [ // Consumidor Final: RUC opcional o Cédula
    { value: '01', label: 'RUC - Registro Único del Contribuyente' },
    { value: '02', label: 'Cédula - Cédula de Identidad Personal' },
  ],
  '03': [ // Gobierno: solo RUC
    { value: '01', label: 'RUC - Registro Único del Contribuyente' },
  ],
};

// Tipo de RUC (Natural o Jurídico)
const RUC_PERSON_TYPES = [
  { value: '1', label: 'Natural' },
  { value: '2', label: 'Jurídico' },
];

export function Step1CustomerSelection({ wizardData, onUpdate, onNext }: Step1CustomerSelectionProps) {
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isBranchOfficeModalOpen, setIsBranchOfficeModalOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null); // Store selected customer for address modal
  // Destinatarios de notificación: arranca oculto (es un campo opcional, no
  // necesita ocupar espacio por defecto). Se revela con "+ Agregar", o solo
  // si ya trae datos reales (p.ej. el cliente seleccionado traía correo).
  const [isNotificationsExpanded, setIsNotificationsExpanded] = useState(false);

  // Obtener datos de la compañía seleccionada y sucursales
  const { selectedCompany, isLoading: companyLoading, refetchSelectedCompany } = useCompanies({ navigateOnSelect: false });
  const { data: branchOfficesData, isLoading: loadingBranches } = useBranchOffices();
  const { data: countries } = useCountries();

  // Refetch selected company data when component mounts to ensure fresh data
  useEffect(() => {
    console.log('🔄 Step1 montado - Refrescando datos de compañía seleccionada...');
    refetchSelectedCompany();
  }, [refetchSelectedCompany]); // Solo al montar

  // Extraer el array de sucursales del objeto de respuesta
  const branchOffices = ((branchOfficesData as any)?.branchOffices || []).filter((b: any) => b.isActive !== false);

  console.log('🏢 Step1 - Company:', selectedCompany);
  console.log('🏢 Step1 - Branch Offices Data:', branchOfficesData);
  console.log('🏢 Step1 - Branch Offices Array:', branchOffices);
  if (branchOffices.length > 0) {
    console.log('🏢 Step1 - Sample Branch Office:', branchOffices[0]);
  }

  // Update wizard data when company is selected
  useEffect(() => {
    if (selectedCompany && !companyLoading) {
      console.log('📝 Updating wizard data with company info:', selectedCompany);
      console.log('📝 Current issuerName in wizard:', wizardData.issuerName);

      // Solo actualizar si los datos del emisor están vacíos o son diferentes
      // Esto evita sobrescribir datos cuando ya están cargados
      // Usar fullName (razón social) para issuerName, no name (nombre comercial)
      const companyFullName = selectedCompany.fullName || selectedCompany.name || '';
      const shouldUpdate = !wizardData.issuerName ||
                          wizardData.issuerName !== companyFullName ||
                          !wizardData.issuerCode ||
                          wizardData.issuerCode !== selectedCompany.numberId ||
                          !wizardData.issuerCompanyCode ||
                          wizardData.issuerCompanyCode !== selectedCompany.code;

      if (shouldUpdate) {
        console.log('✅ Actualizando datos del emisor en el wizard');

        // Convertir TypeTaxpayer a string para el mapper
        const typeTaxpayer = selectedCompany.typeTaxpayer?.toString() || '2';

        onUpdate({
          issuerName: companyFullName,
          issuerCode: selectedCompany.numberId || '',
          issuerCompanyCode: selectedCompany.code || '',
          issuerDv: selectedCompany.dv || '',
          issuerTypeTaxpayer: typeTaxpayer, // 1=Natural, 2=Jurídico
          issuerEmail: selectedCompany.email || '',
          issuerPhone: selectedCompany.phone || '',
          issuerImage: selectedCompany.profileImage || '',
        });
      } else {
        console.log('ℹ️ Datos del emisor ya están actualizados, saltando actualización');
      }
    } else if (!selectedCompany && !companyLoading) {
      console.warn('⚠️ No hay compañía seleccionada después de cargar');
    }
  }, [selectedCompany, companyLoading, wizardData.issuerName, wizardData.issuerCode]);

  // Update wizard data when branch office is selected - FETCH FROM ALUDRA API
  useEffect(() => {
    const fetchBranchDetails = async () => {
      console.log('🔍 useEffect ejecutado - branchOfficeId:', wizardData.branchOfficeId, 'companyId:', selectedCompany?.id);

      if (wizardData.branchOfficeId && selectedCompany?.id) {
        try {
          console.log('🔄 Obteniendo detalles completos de la sucursal desde Aludra...');
          console.log('🔄 Parámetros: CompanyId =', selectedCompany.id, ', BranchOfficeId =', wizardData.branchOfficeId);

          // Llamar al API de Aludra para obtener detalles completos
          const branchDetails = await branchOfficeService.getBranchOfficeDetailsFromAludra(
            selectedCompany.id,
            wizardData.branchOfficeId
          );

          console.log('📝 Actualizando wizard con detalles de Aludra:', branchDetails);

          // Capturar CountryId del BranchOffice
          const countryId = branchDetails.CountryId || branchDetails.Address_CountryId;
          console.log('🌍 CountryId del BranchOffice:', countryId);

          // Valores por defecto (Panamá)
          let countryCode = 'PA';
          let countryName = 'PANAMÁ';

          // Si hay CountryId, buscar la información del país
          if (countryId) {
            try {
              console.log('🔄 Obteniendo información del país desde Aludra...');
              const countryData = await companyService.getCountryById(countryId);

              if (countryData?.Data && countryData.Data.length > 0) {
                const country = countryData.Data[0];
                countryCode = country.Code || 'PA';
                countryName = country.Name || 'PANAMÁ';
                console.log('✅ País obtenido:', { countryCode, countryName });
              }
            } catch (error) {
              console.warn('⚠️ Error al obtener información del país, usando Panamá por defecto:', error);
            }
          } else {
            console.log('ℹ️ CountryId es NULL, usando Panamá por defecto');
          }

          // Mapear los campos de Aludra al formato del wizard
          const updateData = {
            branchOfficeName: branchDetails.Description || branchDetails.Name || '',
            branchOfficeAddress: branchDetails.Address_AddressDetail || branchDetails.Address || '',
            branchOfficeAddressCode: branchDetails.Code || '',
            branchOfficeLatitude: branchDetails.Address_Latitude || '',
            branchOfficeLongitude: branchDetails.Address_Longitude || '',
            branchOfficeProvince: branchDetails.Address_ProvinceName || branchDetails.Address_Province || branchDetails.Province || '',
            branchOfficeDistrict: branchDetails.Address_DistrictName || branchDetails.Address_District || branchDetails.District || '',
            branchOfficeTownship: branchDetails.Address_TownshipName || branchDetails.Address_Township || branchDetails.Township || '',
            branchOfficeFiscalCode: branchDetails.Address_FiscalCode || branchDetails.AddressFiscalCode || branchDetails.FiscalCode || '',
            branchOfficeCountryId: countryId || '',
            branchOfficeCountryCode: countryCode,
            branchOfficeCountryName: countryName,
          };

          console.log('🔄 DATOS QUE SE VAN A ACTUALIZAR EN EL WIZARD:', JSON.stringify(updateData, null, 2));

          onUpdate(updateData);

          console.log('✅ Datos de sucursal actualizados correctamente');
        } catch (error) {
          console.error('❌ Error al obtener detalles de la sucursal:', error);

          // Fallback: usar datos locales si el API falla
          const selectedBranch = branchOffices.find((b: any) => b.id === wizardData.branchOfficeId);
          if (selectedBranch) {
            console.warn('⚠️ Usando datos locales de la sucursal (fallback)');
            onUpdate({
              branchOfficeName: selectedBranch.name || '',
              branchOfficeAddress: selectedBranch.address || '',
              branchOfficeAddressCode: selectedBranch.addressCode || selectedBranch.code || '',
              branchOfficeLatitude: selectedBranch.coordinates?.latitude?.toString() || '',
              branchOfficeLongitude: selectedBranch.coordinates?.longitude?.toString() || '',
              branchOfficeProvince: selectedBranch.province || '',
              branchOfficeDistrict: selectedBranch.district || '',
              branchOfficeTownship: selectedBranch.township || '',
              branchOfficeFiscalCode: selectedBranch.fiscalCode || '',
            });
          }
        }
      }
    };

    fetchBranchDetails();
  }, [wizardData.branchOfficeId, selectedCompany, branchOffices]);

  const handleSelectCustomer = (customer: any) => {
    // Store the selected customer for address modal
    setSelectedCustomer(customer);

    // Debug log to see what we're receiving
    console.log('📋 Customer selected:', {
      customerKind: customer.customerKind,
      recepType: customer.recepType,
      fullCustomer: customer
    });

    // Get the default billing address or the first address
    const defaultAddress = customer.address?.find((addr: any) => addr.is_default && addr.type === 'BILLING')
      || customer.address?.[0];

    // Tipo de receptor DGI (iTipoRec: 01=Contribuyente, 02=ConsumidorFinal,
    // 03=Gobierno, 04=Extranjero) — viene directo de `customer.recepType`
    // desde la migración a mdl03/core (antes se derivaba de `taxIdType`,
    // que ya no existe en el shape del cliente).
    const receiverType = customer.recepType || '01';

    // Tipo de documento (RUC/Cédula/Pasaporte/Otro): el formulario dual
    // Particular/Comercial no captura este dato por separado — se infiere
    // del tipo de cliente (Comercial siempre factura con RUC; Particular con
    // cédula/pasaporte). Simplificación aceptada frente al modelo anterior,
    // que sí tenía un selector explícito.
    const customerRucType = customer.customerKind === 'BUSINESS' ? '01' : '02';

    const customerName = displayCustomerName(customer);
    const customerTaxId = displayCustomerTaxId(customer);

    // Resolver código ISO del país a partir del nombre
    const countryName = defaultAddress?.country || '';
    const matchedCountry = countries?.find(
      (c) => c.Name.toUpperCase() === countryName.toUpperCase()
    );
    const countryCode = matchedCountry?.Code || '';

    onUpdate({
      customerId: customer.id,
      customerName,
      customerRuc: customerTaxId,
      customerDv: customer.dv || '',
      customerEmail: customer.email || '',
      customerPhone: customer.phone || '',
      // Por defecto, el destinatario de notificación es el correo del cliente seleccionado
      // (el usuario puede agregar más destinatarios desde la sección de notificaciones)
      notificationReceivers: customer.email
        ? [{ Email: customer.email, Name: customerName || '' }]
        : [],
      receiverType,
      customerRucType,
      customerRecepType: customer.customerKind === 'BUSINESS' ? '2' : '1',
      // Address fields
      customerAddress: defaultAddress?.line1 || '',
      customerCountry: countryName,
      customerForeignCountry: countryCode, // Código ISO del país (ej: GT, CR, US)
      customerProvince: defaultAddress?.state || '',
      customerDistrict: defaultAddress?.district || '',
      customerTownship: defaultAddress?.township || defaultAddress?.town || '',
      customerCity: defaultAddress?.city || '',
      customerAddressFiscalCode: defaultAddress?.code_address || '',
      // Auto-ajustar destino de operación según tipo de receptor
      // No forzar destino para extranjero; se define en Step 2 según el tipo de operación
    });
    setIsCustomerModalOpen(false);
  };

  const handleAddressUpdate = (addressData: any) => {
    onUpdate({
      customerAddress: addressData.description,
      customerCountry: addressData.country,
      customerForeignCountry: addressData.countryCode || '', // Código ISO del país para clientes extranjeros
      customerProvince: addressData.province,
      customerDistrict: addressData.district,
      customerTownship: addressData.township,
      customerCity: addressData.city || '',
      customerAddressFiscalCode: addressData.fiscalCode,
    });
    setIsAddressModalOpen(false);
  };

  // Destinatarios de notificación por email (uno o más) — independientes del correo fiscal del receptor.
  // Si aún no se ha capturado ninguno, se muestra una fila vacía para editar.
  const notificationReceivers: EmailReceiver[] =
    wizardData.notificationReceivers && wizardData.notificationReceivers.length > 0
      ? wizardData.notificationReceivers
      : [{ Email: '', Name: '' }];

  const handleNotificationReceiverChange = (index: number, field: keyof EmailReceiver, value: string) => {
    const updated = [...notificationReceivers];
    updated[index] = { ...updated[index], [field]: value };
    onUpdate({ notificationReceivers: updated });
  };

  const addNotificationReceiver = () => {
    onUpdate({ notificationReceivers: [...notificationReceivers, { Email: '', Name: '' }] });
  };

  const removeNotificationReceiver = (index: number) => {
    const updated = notificationReceivers.filter((_, i) => i !== index);
    onUpdate({ notificationReceivers: updated });
    // Si era el último destinatario, no queda nada que editar: se vuelve a
    // ocultar toda la sección (mismo estado inicial, antes de "Agregar").
    if (updated.length === 0) {
      setIsNotificationsExpanded(false);
    }
  };

  // Si ya trae destinatarios reales (p.ej. el cliente seleccionado tenía
  // correo, ver handleSelectCustomer), la sección se revela sola — nunca
  // debe esconder datos que el usuario ya cargó.
  useEffect(() => {
    if ((wizardData.notificationReceivers || []).some((r) => r.Email?.trim() || r.Name?.trim())) {
      setIsNotificationsExpanded(true);
    }
  }, [wizardData.notificationReceivers]);


  // Validación en vivo: mostrar errores DGI condicionales mientras el usuario edita
  useEffect(() => {
    setErrors(prev => {
      const updated = { ...prev };

      // Limpiar errores que ya se corrigieron
      if (wizardData.customerName && updated.customerName) delete updated.customerName;
      if (wizardData.customerAddress && updated.customerAddress) delete updated.customerAddress;
      if (wizardData.customerProvince && updated.customerProvince) delete updated.customerProvince;
      if (wizardData.customerEmail && updated.customerEmail) delete updated.customerEmail;
      if (
        updated.notificationReceivers &&
        (wizardData.notificationReceivers || []).some((r) => r.Email?.trim())
      ) {
        delete updated.notificationReceivers;
      }
      if (wizardData.customerRuc && updated.customerRuc) delete updated.customerRuc;
      if (wizardData.customerDv && updated.customerDv) delete updated.customerDv;
      if (wizardData.branchOfficeId && updated.branchOffice) delete updated.branchOffice;

      // Solo mostrar errores DGI condicionales si el usuario ya seleccionó un cliente
      if (wizardData.customerId) {
        // DGI B404: Dirección obligatoria solo para Contribuyente (01) y Gobierno (03), opcional para 02/04
        const needsAddress = wizardData.receiverType === '01' || wizardData.receiverType === '03';
        if (needsAddress && !wizardData.customerAddress) {
          updated.customerAddress = 'Dirección obligatoria para el receptor (DGI B404)';
        }
        if (!wizardData.customerForeignCountry?.trim()) {
          updated.customerCountry = 'País del receptor obligatorio (DGI B410)';
        }
        // DGI B405: Ubicación solo obligatoria para Contribuyente (01) y Gobierno (03)
        const isContrib = wizardData.receiverType === '01';
        const isGob = wizardData.receiverType === '03';
        if ((isContrib || isGob) && !wizardData.customerProvince) {
          updated.customerProvince = 'Ubicación obligatoria para Contribuyente/Gobierno (DGI B405)';
        }

        // Limpiar errores de país antes de re-evaluar
        delete updated.customerCountry;

        // NOTA: B410a y B410b no se validan aquí porque el destino se configura en Step 2.
        // Estas validaciones se ejecutan en el invoiceMapper antes de enviar a Aludra.
      }

      // DGI B08 (1515): Punto de facturación obligatorio y distinto de "000" → R
      if (!wizardData.billingPointCode) {
        updated.billingPoint = 'El punto de facturación es requerido';
      } else if (wizardData.billingPointCode === '000') {
        updated.billingPoint = 'El punto de facturación no puede ser "000" (DGI B08 código 1515)';
      } else if (updated.billingPoint) {
        delete updated.billingPoint;
      }

      return updated;
    });
  }, [wizardData.receiverType, wizardData.customerName, wizardData.customerAddress, wizardData.customerProvince, wizardData.customerEmail, wizardData.notificationReceivers, wizardData.customerRuc, wizardData.customerDv, wizardData.branchOfficeId, wizardData.customerId, wizardData.operationDestination, wizardData.customerForeignCountry, wizardData.billingPointCode]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Validación de la sucursal (obligatoria) — se valida aquí, al elegirla,
    // en vez de esperar hasta el envío final: dSucEm compone el CUFE (DGI
    // B301: A 4) y un código inválido hace que la factura nunca se pueda
    // corregir con "mismo CUFE" después. Mejor bloquear antes de que el
    // usuario llene el resto del formulario.
    if (!wizardData.branchOfficeId) {
      newErrors.branchOffice = 'Debe seleccionar una sucursal';
    } else if (wizardData.isFixingEBill) {
      // Corrección "mismo CUFE": solo permitido si el código original ya
      // era válido, o si la sucursal elegida es la MISMA (mismo
      // identificador) solo con los ceros a la izquierda que le faltaban.
      const resolved = resolveCorrectionBranchOfficeCode(
        wizardData.originalBranchOfficeCode,
        wizardData.branchOfficeAddressCode
      );
      if (resolved.blocked) {
        newErrors.branchOffice = resolved.reason || 'El código de la sucursal no es válido para esta corrección.';
      }
    } else if (!isValidBranchOfficeCode(wizardData.branchOfficeAddressCode)) {
      newErrors.branchOffice =
        'Esta sucursal tiene un código inválido (debe tener exactamente 4 caracteres). Corrígela en Sucursales antes de facturar.';
    }

    const isContribuyente = wizardData.receiverType === '01';
    const isGobierno = wizardData.receiverType === '03';

    // --- Validaciones del Receptor ---

    // DGI B403: Nombre obligatorio si tipo = 01, 03 o 04 (R), opcional para 02
    const isExtranjero = wizardData.receiverType === '04';
    if ((isContribuyente || isGobierno || isExtranjero) && !wizardData.customerName) {
      newErrors.customerName = 'Nombre del receptor es obligatorio (DGI B403)';
    }

    // RUC/DV: requerido para Contribuyente y Gobierno (DGI B402)
    if ((isContribuyente || isGobierno) && !wizardData.customerRuc) {
      newErrors.customerRuc = 'El RUC es requerido para Contribuyente/Gobierno';
    }
    if ((isContribuyente || isGobierno) && !wizardData.customerDv) {
      newErrors.customerDv = 'El DV es requerido para Contribuyente/Gobierno';
    }
    if (wizardData.customerDv && wizardData.customerDv.length > 2) {
      newErrors.customerDv = 'El DV no puede tener más de 2 dígitos';
    }

    // DGI B402d/B406 no se validan aquí para mantener el mismo flujo del portal legacy

    // DGI B404: Dirección obligatoria solo para Contribuyente (01) y Gobierno (03), opcional para 02/04
    if ((isContribuyente || isGobierno) && !wizardData.customerAddress) {
      newErrors.customerAddress = 'Dirección obligatoria para el receptor (DGI B404)';
    }

    // DGI B405: Ubicación (provincia/distrito/corregimiento) obligatoria solo para
    // Contribuyente (01) y Gobierno (03), NO para Consumidor Final (02) ni Extranjero (04)
    if ((isContribuyente || isGobierno) && !wizardData.customerProvince) {
      newErrors.customerProvince = 'Ubicación obligatoria para Contribuyente/Gobierno (DGI B405)';
    }

    // Validación de email — DGI B409: Ocu 0-3 (siempre opcional para DGI)
    // Para el portal: requerido para Contribuyente/Gobierno/Extranjero (para envío de FE), opcional para Consumidor Final
    if (!wizardData.customerEmail) {
      newErrors.customerEmail = 'El correo electrónico es requerido para enviar la factura';
    } else if (wizardData.customerEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
      if (!emailRegex.test(wizardData.customerEmail)) {
        newErrors.customerEmail = 'El formato del correo electrónico no es válido';
      }
    }

    // Validación de destinatarios de notificación (correos a los que se envía la factura al emitirla).
    // Es opcional: el receptor fiscal ya recibe la notificación automáticamente,
    // así que solo se valida el formato de los correos que sí se hayan cargado.
    const validNotificationReceivers = (wizardData.notificationReceivers || []).filter((r) => r.Email?.trim());
    if (validNotificationReceivers.length > 0) {
      const notificationEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
      const invalidNotificationEmails = validNotificationReceivers.filter(
        (r) => !notificationEmailRegex.test(r.Email)
      );
      if (invalidNotificationEmails.length > 0) {
        newErrors.notificationReceivers = 'Uno o más correos de notificación no tienen un formato válido';
      }
    }

    // --- Validaciones de País ---

    if (!wizardData.customerForeignCountry?.trim()) {
      newErrors.customerCountry = 'País del receptor obligatorio (DGI B410)';
    }

    // NOTA: Las validaciones B410a (país=PA si destino=Panamá) y B410b (país≠PA si destino=Extranjero)
    // NO se validan aquí porque el destino se configura en Step 2.
    // Estas validaciones se ejecutan en el invoiceMapper antes de enviar a Aludra.

    // DGI B08 (1515): Punto de facturación obligatorio y distinto de "000" -> R
    if (!wizardData.billingPointCode) {
      newErrors.billingPoint = 'El punto de facturación es requerido';
    } else if (wizardData.billingPointCode === '000') {
      newErrors.billingPoint = 'El punto de facturación no puede ser "000" (DGI B08)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  const displayAddress = () => {
    const parts = [];
    if (wizardData.customerCountry) parts.push(wizardData.customerCountry);
    if (wizardData.customerProvince) parts.push(wizardData.customerProvince);
    if (wizardData.customerDistrict) parts.push(wizardData.customerDistrict);
    if (wizardData.customerTownship) parts.push(wizardData.customerTownship);
    return parts.join(', ') || wizardData.customerAddress || 'Seleccione la dirección';
  };

  const emisorInitials = (selectedCompany?.name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w: string) => w[0])
    .join('')
    .toUpperCase();

  /*
   * Presentación (Claude Design): se probó una versión "hoja de factura" con
   * cabecera de documento (FACTURA / N.º / Fecha) y pie con CUFE placeholder,
   * pero esos dos bloques son 100% decorativos en este paso —el documento ni
   * existe todavía— y sumaban ~200px de alto sin aportar ningún dato real,
   * forzando scroll para llegar al formulario. Se volvió a las dos tarjetas
   * De/Emisor · A/Receptor una al lado de la otra (mismo layout que ya
   * funcionaba), conservando los acentos de color, tipografía y bordes
   * punteados del sistema, con Contacto y Ubicación dentro de la misma
   * tarjeta del receptor en vez de bandas propias a todo el ancho.
   */
  return (
    <div className="font-display" data-testid="invoice-wizard-step1-container">
      {/* Alerta cuando no hay compañía seleccionada */}
      {!selectedCompany && !companyLoading && (
        <div className="mb-4 rounded-lg border-l-4 border-yellow-400 bg-yellow-50 p-4 dark:bg-yellow-900/20" data-testid="invoice-wizard-step1-no-company-alert">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                No hay compañía seleccionada
              </h3>
              <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                <p>
                  Por favor selecciona una compañía desde el selector en la esquina superior derecha de la página.
                  Sin una compañía seleccionada, no se podrá completar la información del emisor.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============= EMISOR | RECEPTOR ============= */}
      <div className="relative overflow-hidden rounded-2xl border border-[#ececec] bg-white shadow-[0_1px_2px_rgba(10,34,51,.03),0_16px_40px_-24px_rgba(10,34,51,.18)] dark:border-secondary-700 dark:bg-secondary-800">
        <div aria-hidden="true" className="h-1 bg-gradient-to-r from-teal-500 via-[#3a7ac7] to-[#6b239e]" />

        {/*
          El título del paso vivía en un bloque aparte arriba de la tarjeta
          (h2 + descripción + su propio margen) — ~70px que se sumaban al
          alto total. Ahora es una banda de título compacta dentro de la
          misma tarjeta, título y descripción en una sola línea en pantallas
          anchas.
        */}
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-dashed border-secondary-200 px-5 py-3 dark:border-secondary-700 sm:px-6">
          <h2 className="text-[15px] font-bold tracking-[-0.01em] text-ink-850 dark:text-white">
            Información del Emisor y Receptor
          </h2>
          <p className="text-[12px] text-secondary-500 dark:text-secondary-400">
            Complete la información de la compañía emisora y el receptor de la factura
          </p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_1px_1fr]">
          {/* ==== DE · EMISOR ==== */}
          <section className="px-5 py-5 sm:px-6 xl:pr-6">
            {/* min-h fija: sin esto, "ACTIVO" (badge chico) y "Buscar cliente"
                (boton, mas alto) le dan distinta altura a cada cabecera y el
                membrete/nombre de abajo arrancan desalineados entre columnas
                desde el breakpoint xl (donde recien se ven lado a lado). */}
            <div className="mb-4 flex min-h-[30px] items-center justify-between gap-3">
              <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em] text-[#6b239e]">
                <span className="h-[6px] w-[6px] rounded-full bg-[#6b239e]" />
                De · Emisor
              </div>
              {selectedCompany && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0d9488]/10 px-2 py-[2px] text-[10px] font-bold tracking-[0.06em] text-[#0d9488]">
                  <span className="h-[4px] w-[4px] rounded-full bg-[#0d9488]" />
                  ACTIVO
                </span>
              )}
            </div>

            {/* Membrete: logo real de la compañía o iniciales */}
            <div className="flex items-center gap-3">
              {selectedCompany?.profileImage ? (
                <img
                  src={selectedCompany.profileImage}
                  alt={`Logo de ${selectedCompany.name}`}
                  className="h-11 w-11 flex-shrink-0 rounded-xl bg-white object-contain p-1"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <span
                className={`inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-[#6b239e] text-base font-extrabold tracking-[-0.03em] text-white ${selectedCompany?.profileImage ? 'hidden' : ''}`}
              >
                {emisorInitials || '—'}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[16px] font-extrabold leading-tight tracking-[-0.015em] text-ink-850 dark:text-white">
                  {selectedCompany?.name || (companyLoading ? 'Cargando…' : 'No seleccionada')}
                </div>
                <div className="mt-0.5 font-mono text-[11px] text-secondary-400">
                  Código {selectedCompany?.code || (companyLoading ? '…' : 'N/A')}
                </div>
                {!selectedCompany && !companyLoading && (
                  <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">
                    ⚠️ Por favor selecciona una compañía desde el selector en la esquina superior
                  </p>
                )}
              </div>
            </div>

            {/* Datos fiscales del emisor: son de solo lectura, se muestran como
                lineas de dato en vez de inputs deshabilitados. */}
            <div className="mt-4 flex flex-col gap-2.5">
              <div className="grid grid-cols-[84px_1fr] items-baseline gap-3">
                <div className="text-xs font-semibold text-ink-850 dark:text-white">RUC</div>
                <div className="font-mono text-[14px] font-semibold text-ink-850 dark:text-white">
                  {selectedCompany?.numberId || (companyLoading ? 'Cargando…' : 'N/A')}
                  <span className="text-secondary-400">
                    {' '}· DV {selectedCompany?.dv || (companyLoading ? '…' : 'N/A')}
                  </span>
                </div>
              </div>
              <div
                aria-hidden="true"
                className="h-px"
                style={{ background: 'repeating-linear-gradient(90deg, #e2e8f0 0 4px, transparent 4px 8px)' }}
              />

              <div className="grid grid-cols-[84px_1fr] items-center gap-3">
                <label htmlFor="branchOffice" className="text-xs font-semibold text-ink-850 dark:text-white">
                  Sucursal <span className="font-semibold text-[#b8503a]">*</span>
                </label>
                <div className="flex min-w-0 gap-1.5">
                  <select data-testid="invoice-wizard-step1-branch-office-select"
                    id="branchOffice"
                    className={`min-w-0 flex-1 rounded-[10px] border bg-white px-3 py-2.5 text-[14px] font-medium text-ink-850 outline-none transition-colors dark:bg-secondary-700 dark:text-white ${
                      errors.branchOffice
                        ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500'
                        : 'border-[#ececec] hover:border-secondary-300 focus:border-secondary-400 focus:ring-[3px] focus:ring-secondary-400/[.14] dark:border-secondary-600'
                    }`}
                    value={wizardData.branchOfficeId}
                    onChange={(e) => {
                      const selectedBranch = branchOffices.find((b: any) => b.id === e.target.value);
                      onUpdate({
                        branchOfficeId: e.target.value,
                        // Setear coordenadas inmediato desde datos locales (fallback síncrono)
                        // El useEffect async de Aludra las sobrescribirá con datos frescas
                        branchOfficeLatitude: selectedBranch?.coordinates?.latitude?.toString() || '',
                        branchOfficeLongitude: selectedBranch?.coordinates?.longitude?.toString() || '',
                      });
                      // Clear error when user selects a branch
                      if (errors.branchOffice) {
                        setErrors({ ...errors, branchOffice: '' });
                      }
                    }}
                    disabled={loadingBranches || !selectedCompany}
                  >
                    <option value="">
                      {loadingBranches
                        ? 'Cargando sucursales...'
                        : !selectedCompany
                        ? 'Seleccione una compañía primero'
                        : branchOffices && branchOffices.length === 0
                        ? 'No hay sucursales configuradas'
                        : 'Seleccione una sucursal'}
                    </option>
                    {branchOffices?.map((branch: any) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}{branch.fiscalCode ? ` - ${branch.fiscalCode}` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setIsBranchOfficeModalOpen(true)}
                    data-testid="invoice-wizard-step1-create-branch-office-button"
                    disabled={!selectedCompany}
                    title="Crear nueva sucursal"
                    aria-label="Crear nueva sucursal"
                    className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-ink-850 text-white transition-colors hover:bg-[#142d40] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <PlusIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              {errors.branchOffice && (
                <p className="text-xs text-danger-600 dark:text-danger-400" data-testid="invoice-wizard-step1-branch-office-error">{errors.branchOffice}</p>
              )}
              {branchOffices && branchOffices.length === 0 && selectedCompany && !errors.branchOffice && (
                <p className="text-xs text-yellow-600 dark:text-yellow-400">
                  ⚠️ Esta compañía no tiene sucursales configuradas. Contacte al administrador.
                </p>
              )}

              <div
                aria-hidden="true"
                className="h-px"
                style={{ background: 'repeating-linear-gradient(90deg, #e2e8f0 0 4px, transparent 4px 8px)' }}
              />

              <div className="grid grid-cols-[84px_1fr] items-center gap-3">
                <label htmlFor="billingPointCode" className="text-xs font-semibold text-ink-850 dark:text-white">
                  Punto fact. <span className="font-semibold text-[#b8503a]">*</span>
                </label>
                <Input
                  id="billingPointCode"
                  value={wizardData.billingPointCode}
                  onChange={(e) => {
                    // DGI B08: A 3, solo alfanumérico
                    const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                    onUpdate({ billingPointCode: val });
                  }}
                  onBlur={(e) => {
                    // Rellenar a la izquierda con ceros hasta 3 dígitos
                    const val = e.target.value;
                    if (val && val.length < 3) {
                      onUpdate({ billingPointCode: val.padStart(3, '0') });
                    }
                  }}
                  maxLength={3}
                  placeholder="001"
                  className={`w-[74px] px-2.5 py-2 text-center font-mono text-sm font-bold tracking-[0.08em] ${errors.billingPoint ? 'border-danger-600' : ''}`}
                 data-testid="invoice-wizard-step1-billing-point-input"/>
              </div>
              {errors.billingPoint && (
                <p className="text-xs text-danger-600 dark:text-danger-400" data-testid="invoice-wizard-step1-billing-point-error">{errors.billingPoint}</p>
              )}
            </div>
          </section>

          {/* Separador punteado vertical */}
          <div
            aria-hidden="true"
            className="hidden w-px xl:block"
            style={{ background: 'repeating-linear-gradient(180deg, #e2e8f0 0 5px, transparent 5px 10px)' }}
          />
          {/* En una columna el separador va horizontal */}
          <div
            aria-hidden="true"
            className="mx-6 h-px xl:hidden"
            style={{ background: 'repeating-linear-gradient(90deg, #e2e8f0 0 5px, transparent 5px 10px)' }}
          />

          {/* ==== A · RECEPTOR ==== */}
          <section className="px-5 py-5 sm:px-6 xl:pl-6">
            <div className="mb-4 flex min-h-[30px] items-center justify-between gap-3">
              <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em] text-[#0d9488]">
                <span className="h-[6px] w-[6px] rounded-full bg-teal-500" />
                A · Receptor
              </div>
              <button
                type="button"
                onClick={() => setIsCustomerModalOpen(true)}
                data-testid="invoice-wizard-step1-open-customer-modal-button"
                title="Administrador de clientes"
                className="inline-flex items-center gap-2 rounded-[10px] border border-[#ececec] bg-white px-3 py-[7px] text-xs font-medium text-ink-850 transition-colors hover:border-secondary-300 dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
              >
                <MagnifyingGlassIcon className="h-3 w-3" />
                Buscar cliente
              </button>
            </div>

            {/* Nombre del receptor */}
            <div>
              <div>
                <label htmlFor="customerName" className="text-xs font-semibold text-ink-850 dark:text-white">
                  Nombre del receptor{' '}
                  {(wizardData.receiverType === '01' || wizardData.receiverType === '03' || wizardData.receiverType === '04') && (
                    <span className="font-semibold text-[#b8503a]">*</span>
                  )}
                  {wizardData.receiverType === '02' && (
                    <span className="ml-1 text-[12px] font-normal text-secondary-400">(Opcional)</span>
                  )}
                </label>
              </div>
              <div className="mt-1.5 flex gap-2">
                <Input
                  id="customerName"
                  // Controlado, mismo motivo que el correo: con defaultValue + onBlur el
                  // autollenado del navegador no llegaba al estado del asistente.
                  value={wizardData.customerName || ''}
                  onChange={(e) => onUpdate({ customerName: e.target.value })}
                  onBlur={(e) => onUpdate({ customerName: e.target.value.trim() })}
                  placeholder="Escriba el nombre del receptor"
                  maxLength={150}
                  className={`px-3.5 py-[11px] text-[15px] font-semibold tracking-[-0.01em] ${errors.customerName ? 'border-danger-600' : ''}`}
                 data-testid="invoice-wizard-step1-customer-name-input"/>
                <button
                  type="button"
                  onClick={() => setIsCustomerModalOpen(true)}
                  title="Administrador de clientes"
                  aria-label="Agregar receptor"
                  className="inline-flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-[10px] bg-ink-850 text-white transition-colors hover:bg-[#142d40]"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>
              {errors.customerName && <p className="mt-1 text-xs text-danger-600 dark:text-danger-400" data-testid="invoice-wizard-step1-customer-name-error">{errors.customerName}</p>}
            </div>

            {/* Trío de tipos */}
            <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
              <div>
                <label htmlFor="receiverType" className="text-xs font-semibold text-ink-850 dark:text-white">
                  Tipo receptor <span className="font-semibold text-[#b8503a]">*</span>
                </label>
                <div className="relative mt-1.5">
                <select data-testid="invoice-wizard-step1-receiver-type-select"
                  id="receiverType"
                  className="w-full cursor-pointer appearance-none truncate rounded-[10px] border border-[#ececec] bg-white py-2.5 pl-2.5 pr-8 text-[13px] font-medium text-ink-850 outline-none transition-colors hover:border-secondary-300 focus:border-secondary-400 focus:ring-[3px] focus:ring-secondary-400/[.14] dark:border-secondary-600 dark:bg-secondary-700 dark:text-white"
                  value={wizardData.receiverType}
                  onChange={(e) => {
                    const newType = e.target.value;
                    const isNewExtranjero = newType === '04';
                    const isNewConsumidorFinal = newType === '02';
                    onUpdate({
                      receiverType: newType,
                      // No forzar destino al cambiar tipo de receptor
                      // Limpiar campos incompatibles al cambiar tipo de receptor (DGI B406a)
                      ...(isNewExtranjero ? {
                        customerDv: '',
                        customerRecepType: '1',
                        customerForeignId: wizardData.customerRuc || '',
                      } : {}),
                      // Si cambia de Extranjero a otro tipo, limpiar ID extranjero
                      ...(wizardData.receiverType === '04' && !isNewExtranjero ? {
                        customerForeignId: undefined,
                        customerForeignCountry: undefined,
                      } : {}),
                      // Si es Consumidor Final, forzar tipo RUC a Natural (DGI B402d)
                      ...(isNewConsumidorFinal ? { customerRecepType: '1' } : {}),
                      // Resetear tipo de documento a RUC al cambiar tipo de receptor
                      // (evita que quede Pasaporte/Otro seleccionado para Contribuyente/Gobierno)
                      customerRucType: '01',
                    });
                    // Limpiar errores al cambiar tipo
                    setErrors({});
                  }}
                >
                  {RECEIVER_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <svg aria-hidden="true" className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-secondary-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
                </div>
              </div>

              {/* Tipo de RUC - Ocultar para Extranjero, bloquear para Consumidor Final */}
              {wizardData.receiverType !== '04' && (
                <div>
                  <label htmlFor="customerRecepType" className="text-xs font-semibold text-ink-850 dark:text-white">
                    Tipo RUC <span className="font-semibold text-[#b8503a]">*</span>
                  </label>
                  <div className="relative mt-1.5">
                  <select data-testid="invoice-wizard-step1-ruc-person-type-select"
                    id="customerRecepType"
                    className={`w-full cursor-pointer appearance-none truncate rounded-[10px] border bg-white py-2.5 pl-2.5 pr-8 text-[13px] font-medium text-ink-850 outline-none transition-colors dark:bg-secondary-700 dark:text-white ${
                      errors.customerRecepType ? 'border-danger-500' : 'border-[#ececec] hover:border-secondary-300 focus:border-secondary-400 focus:ring-[3px] focus:ring-secondary-400/[.14] dark:border-secondary-600'
                    } ${wizardData.receiverType === '02' ? 'opacity-60' : ''}`}
                    value={wizardData.customerRecepType}
                    onChange={(e) => onUpdate({ customerRecepType: e.target.value })}
                    disabled={wizardData.receiverType === '02'}
                  >
                    {RUC_PERSON_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  <svg aria-hidden="true" className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-secondary-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                  </div>
                </div>
              )}

              {/* Tipo de Documento — según tipo de receptor DGI */}
              {wizardData.receiverType !== '04' && (
                <div>
                  <label htmlFor="customerRucType" className="text-xs font-semibold text-ink-850 dark:text-white">
                    Tipo doc. <span className="font-semibold text-[#b8503a]">*</span>
                  </label>
                  <div className="relative mt-1.5">
                  <select data-testid="invoice-wizard-step1-document-type-select"
                    id="customerRucType"
                    title={(DOCUMENT_TYPES_BY_RECEIVER[wizardData.receiverType] || DOCUMENT_TYPES_BY_RECEIVER['01']).find((t) => t.value === wizardData.customerRucType)?.label}
                    className="w-full cursor-pointer appearance-none truncate rounded-[10px] border border-[#ececec] bg-white py-2.5 pl-2.5 pr-8 text-[13px] font-medium text-ink-850 outline-none transition-colors hover:border-secondary-300 focus:border-secondary-400 focus:ring-[3px] focus:ring-secondary-400/[.14] dark:border-secondary-600 dark:bg-secondary-700 dark:text-white"
                    value={wizardData.customerRucType}
                    onChange={(e) => onUpdate({ customerRucType: e.target.value })}
                  >
                    {(DOCUMENT_TYPES_BY_RECEIVER[wizardData.receiverType] || DOCUMENT_TYPES_BY_RECEIVER['01']).map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  <svg aria-hidden="true" className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-secondary-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                  </div>
                </div>
              )}
            </div>

            {/* Notas de las reglas DGI que aplican al trío */}
            {wizardData.receiverType === '02' && (
              <p className="mt-1.5 text-xs text-secondary-500">Consumidor Final solo puede ser Natural</p>
            )}
            {errors.customerRecepType && (
              <p className="mt-1 text-xs text-danger-600 dark:text-danger-400" data-testid="invoice-wizard-step1-ruc-person-type-error">{errors.customerRecepType}</p>
            )}
            {wizardData.receiverType === '02' && wizardData.customerRucType === '02' && (
              <p className="mt-1.5 text-xs text-secondary-500">
                DGI permite enviar cédula en lugar de RUC para Consumidor Final (B4022)
              </p>
            )}

            {/* RUC/ID y DV - Adaptativo segun tipo de receptor */}
            {wizardData.receiverType === '04' ? (
              /* Extranjero: mostrar campo de ID extranjero sin DV */
              <div className="mt-3">
                <label htmlFor="customerRuc" className="text-xs font-semibold text-ink-850 dark:text-white">
                  ID Extranjero (Pasaporte o ID Tributario) <span className="font-semibold text-[#b8503a]">*</span>
                </label>
                <Input
                  id="customerRuc"
                  value={wizardData.customerRuc || ''}
                  onChange={(e) => onUpdate({ customerRuc: e.target.value })}
                  placeholder="Ej: 081-101182-0004L"
                  maxLength={50}
                  className={`mt-1.5 font-mono ${errors.customerRuc ? 'border-danger-600' : ''}`}
                 data-testid="invoice-wizard-step1-foreign-id-input"/>
                {errors.customerRuc && <p className="mt-1 text-xs text-danger-600 dark:text-danger-400" data-testid="invoice-wizard-step1-foreign-id-error">{errors.customerRuc}</p>}
              </div>
            ) : (
              /* Contribuyente/Gobierno/Consumidor Final: RUC + DV */
              <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-[2.4fr_1fr]">
                <div>
                  <label htmlFor="customerRuc" className="text-xs font-semibold text-ink-850 dark:text-white">
                    {wizardData.receiverType === '02' ? 'RUC o Cédula' : 'RUC'}{' '}
                    {(wizardData.receiverType === '01' || wizardData.receiverType === '03') && (
                      <span className="font-semibold text-[#b8503a]">*</span>
                    )}
                    {wizardData.receiverType === '02' && (
                      <span className="ml-1 text-[12px] font-normal text-secondary-400">(Opcional)</span>
                    )}
                  </label>
                  <Input
                    data-testid="invoice-wizard-step1-customer-ruc-input"
                    id="customerRuc"
                    value={wizardData.customerRuc || ''}
                    onChange={(e) => onUpdate({ customerRuc: e.target.value })}
                    placeholder={wizardData.receiverType === '02' ? 'Cédula del ciudadano (opcional)' : 'RUC Ejem: 101991-11-00001'}
                    maxLength={20}
                    className={`mt-1.5 font-mono ${errors.customerRuc ? 'border-danger-600' : ''}`}
                  />
                  {errors.customerRuc && <p className="mt-1 text-xs text-danger-600 dark:text-danger-400" data-testid="invoice-wizard-step1-customer-ruc-error">{errors.customerRuc}</p>}
                </div>

                <div>
                  <label htmlFor="customerDv" className="text-xs font-semibold text-ink-850 dark:text-white">
                    DV{' '}
                    {(wizardData.receiverType === '01' || wizardData.receiverType === '03') && (
                      <span className="font-semibold text-[#b8503a]">*</span>
                    )}
                    {wizardData.receiverType === '02' && (
                      <span className="ml-1 text-[12px] font-normal text-secondary-400">(Opcional)</span>
                    )}
                  </label>
                  <Input
                    data-testid="invoice-wizard-step1-customer-dv-input"
                    id="customerDv"
                    value={wizardData.customerDv || ''}
                    // maxLength no es respetado por los navegadores en <input type="number">,
                    // por eso se trunca manualmente a 2 dígitos también en onChange.
                    onChange={(e) => onUpdate({ customerDv: e.target.value.slice(0, 2) })}
                    placeholder="02"
                    maxLength={2}
                    type="number"
                    className={`mt-1.5 text-center font-mono ${errors.customerDv ? 'border-danger-600' : ''}`}
                  />
                  {errors.customerDv && <p className="mt-1 text-xs text-danger-600 dark:text-danger-400" data-testid="invoice-wizard-step1-customer-dv-error">{errors.customerDv}</p>}
                </div>
              </div>
            )}

            {/* Advertencia para Gobierno: necesitará retención y CPBS */}
            {wizardData.receiverType === '03' && (
              <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
                <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Requisitos para ventas al Gobierno:</p>
                <ul className="mt-1 list-inside list-disc space-y-0.5 text-xs text-blue-600 dark:text-blue-400">
                  <li>Los ítems deben incluir el código CPBS completo (Codificación Panameña de Bienes y Servicios)</li>
                  <li>Se debe configurar la retención aplicable en el paso de notas adicionales</li>
                </ul>
              </div>
            )}

            {/* Regla punteada antes de Contacto/Ubicación: mismos datos que antes
                mostraba § CONTACTO / § UBICACIÓN como bandas a todo el ancho,
                ahora dentro de la misma tarjeta del receptor para no duplicar
                padding ni encabezados de sección. */}
            <div
              aria-hidden="true"
              className="my-4 h-px"
              style={{ background: 'repeating-linear-gradient(90deg, #e2e8f0 0 5px, transparent 5px 10px)' }}
            />

            {/* Correo y teléfono del receptor */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="customerEmail" className="text-xs font-semibold text-ink-850 dark:text-white">
                  Correo electrónico <span className="font-semibold text-[#b8503a]">*</span>
                </label>
                {/*
                  El link de "Agregar destinatario" vive al lado del INPUT (no
                  de la etiqueta): puesto junto a la etiqueta, esa fila queda
                  mas alta que la de "Telefono" (que no tiene nada al lado) y
                  los dos campos arrancaban desalineados. Mismo patron de
                  boton cuadrado que ya usan Nombre del receptor/Sucursal.
                */}
                <div className="mt-1.5 flex gap-2">
                  <div className="flex-1">
                    <Input
                      data-testid="invoice-wizard-step1-customer-email-input"
                      id="customerEmail"
                      type="email"
                      // Controlado (value + onChange), igual que los correos de notificación:
                      // con defaultValue + onBlur el autollenado del navegador no actualizaba
                      // el estado y la validación decía "requerido" hasta hacer clic en el campo.
                      value={wizardData.customerEmail || ''}
                      onChange={(e) => onUpdate({ customerEmail: e.target.value })}
                      onBlur={(e) => onUpdate({ customerEmail: e.target.value.trim() })}
                      placeholder="correo@ejemplo.com"
                      maxLength={50}
                      className={errors.customerEmail ? 'border-danger-600' : ''}
                    />
                  </div>
                  {!isNotificationsExpanded && (
                    <button
                      type="button"
                      onClick={() => setIsNotificationsExpanded(true)}
                      data-testid="invoice-wizard-step1-add-notification-receiver-button"
                      title="Agregar destinatario de notificación"
                      aria-label="Agregar destinatario de notificación"
                      className="inline-flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-[10px] border border-[#ececec] bg-white text-ink-850 transition-colors hover:border-secondary-300 dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
                    >
                      <PlusIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {errors.customerEmail && <p className="mt-1 text-xs text-danger-600 dark:text-danger-400" data-testid="invoice-wizard-step1-customer-email-error">{errors.customerEmail}</p>}
              </div>

              <div>
                <label htmlFor="customerPhone" className="text-xs font-semibold text-ink-850 dark:text-white">
                  Teléfono
                </label>
                <Input
                  id="customerPhone"
                  type="tel"
                  // Controlado: el saneo (solo dígitos, guion y +) se aplica en cada cambio
                  // para que también cubra el autollenado del navegador, que no dispara onBlur.
                  value={wizardData.customerPhone || ''}
                  onChange={(e) => onUpdate({ customerPhone: e.target.value.replace(/[^\d-+]/g, '') })}
                  placeholder="6000-0000 o +507-6000-0000"
                  maxLength={16}
                  className="mt-1.5 font-mono"
                 data-testid="invoice-wizard-step1-customer-phone-input"/>
              </div>
            </div>

            {/* Destinatarios de notificación por email al emitir la factura
                (uno o más). Toda la sección —icono, título, descripción y
                filas— está oculta hasta que se pide desde el link de arriba;
                no es un dato obligatorio y no necesita ocupar espacio por
                defecto. */}
            {isNotificationsExpanded && (
              <div className="mt-3 rounded-lg border border-accent-500/20 bg-gradient-to-br from-highlight-400/10 to-accent-500/5 p-3">
                <div className="flex items-center gap-2.5">
                  <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-highlight-400 text-ink-850">
                    <BellIcon className="h-3 w-3" />
                  </span>
                  <div className="flex min-w-0 flex-1 items-center gap-1.5">
                    <span className="text-[12px] font-bold text-ink-850 dark:text-white">
                      Destinatarios de notificación
                    </span>
                    {/* Info en icono en vez de parrafo fijo: el texto solo
                        importa la primera vez, no debe ocupar una linea
                        propia cada vez que se despliega la seccion. */}
                    <div className="group relative inline-flex">
                      <button
                        type="button"
                        aria-label="Correos adicionales a los que se enviará la factura al emitirla. El correo del receptor ya recibe la notificación automáticamente."
                        className="inline-flex items-center justify-center text-secondary-400 transition-colors hover:text-ink-850 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/40 dark:hover:text-white"
                      >
                        <InformationCircleIcon className="h-3.5 w-3.5" />
                      </button>
                      <div
                        role="tooltip"
                        className="pointer-events-none absolute left-1/2 top-full z-20 mt-1.5 w-56 -translate-x-1/2 rounded-lg bg-ink-850 px-2.5 py-1.5 text-[11px] font-medium leading-snug text-white opacity-0 shadow-[0_8px_20px_-8px_rgba(10,34,51,.5)] transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 dark:bg-secondary-700"
                      >
                        Correos adicionales a los que se enviará la factura al emitirla. El correo del receptor ya
                        recibe la notificación automáticamente.
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addNotificationReceiver}
                    data-testid="invoice-wizard-step1-add-more-notification-receiver-button"
                    className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-[8px] border border-[#ececec] bg-white px-2.5 py-1 text-[11px] font-medium text-ink-850 transition-colors hover:border-secondary-300 dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
                  >
                    <PlusIcon className="h-2.5 w-2.5" />
                    Agregar
                  </button>
                </div>

                <div className="mt-2.5 space-y-2">
                  {notificationReceivers.map((receiver, index) => (
                    <div key={index} className="flex items-start gap-2" data-testid={`invoice-wizard-step1-notification-receiver-row-${index}`}>
                      <div className="flex-1">
                        <Input
                          data-testid={`invoice-wizard-step1-notification-email-input-${index}`}
                          type="email"
                          placeholder="correo@ejemplo.com"
                          value={receiver.Email}
                          onChange={(e) => handleNotificationReceiverChange(index, 'Email', e.target.value)}
                          maxLength={50}
                          className="bg-white px-3 py-2 text-[13px]"
                        />
                      </div>
                      <div className="flex-1">
                        <Input
                          type="text"
                          placeholder="Nombre (opcional)"
                          value={receiver.Name}
                          onChange={(e) => handleNotificationReceiverChange(index, 'Name', e.target.value)}
                          className="bg-white px-3 py-2 text-[13px]"
                         data-testid={`invoice-wizard-step1-notification-name-input-${index}`}/>
                      </div>
                      {/* Siempre visible, incluso con un solo destinatario:
                          borrar el último es lo que vuelve a ocultar toda la
                          sección (ver removeNotificationReceiver). */}
                      <button
                        type="button"
                        onClick={() => removeNotificationReceiver(index)}
                        className="inline-flex items-center px-2 py-2 text-danger-600 hover:text-danger-800"
                        aria-label="Quitar destinatario" data-testid={`invoice-wizard-step1-remove-notification-receiver-button-${index}`}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                {errors.notificationReceivers && (
                  <p className="mt-1 text-xs text-danger-600 dark:text-danger-400" data-testid="invoice-wizard-step1-notification-receivers-error">{errors.notificationReceivers}</p>
                )}
              </div>
            )}

            {/* Dirección del receptor */}
            <div className="mt-3">
              <label htmlFor="customerAddress" className="text-xs font-semibold text-ink-850 dark:text-white">
                Dirección <span className="font-semibold text-[#b8503a]">*</span>
              </label>
              <div className="mt-1.5 flex gap-2">
                <div className="relative flex-1">
                  <MapPinIcon className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-secondary-400" />
                  <Input
                    id="customerAddress"
                    value={displayAddress()}
                    readOnly
                    placeholder="Seleccione la dirección del receptor"
                    className={`cursor-pointer pl-10 ${errors.customerAddress ? 'border-danger-600' : ''}`}
                    onClick={() => setIsAddressModalOpen(true)}
                   data-testid="invoice-wizard-step1-address-input"/>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAddressModalOpen(true)}
                  data-testid="invoice-wizard-step1-open-address-modal-button"
                  title="Seleccionar dirección"
                  aria-label="Seleccionar dirección"
                  className="inline-flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-[10px] bg-ink-850 text-white transition-colors hover:bg-[#142d40]"
                >
                  <MapPinIcon className="h-4 w-4" />
                </button>
              </div>
              {errors.customerAddress && <p className="mt-1 text-xs text-danger-600 dark:text-danger-400" data-testid="invoice-wizard-step1-address-error">{errors.customerAddress}</p>}
              {errors.customerProvince && (
                <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400" data-testid="invoice-wizard-step1-province-warning">{errors.customerProvince}</p>
              )}
              {errors.customerCountry && <p className="mt-1 text-xs text-danger-600 dark:text-danger-400" data-testid="invoice-wizard-step1-country-error">{errors.customerCountry}</p>}
            </div>
          </section>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="mt-5 flex justify-end">
        <Button
          variant="primary"
          onClick={handleNext}
          disabled={!selectedCompany || companyLoading} data-testid="invoice-wizard-step1-next-button"
        >
          Siguiente
        </Button>
      </div>

      {/* Mensaje de ayuda cuando el botón está deshabilitado */}
      {!selectedCompany && !companyLoading && (
        <div className="text-sm text-secondary-500 dark:text-secondary-400 text-right">
          Selecciona una compañía para continuar
        </div>
      )}

      {/* Modals */}
      {isCustomerModalOpen && (
        <CustomerSearchModal
          isOpen={isCustomerModalOpen}
          onClose={() => setIsCustomerModalOpen(false)}
          onSelectCustomer={handleSelectCustomer}
        />
      )}

      {isAddressModalOpen && (
        <AddressModal
          isOpen={isAddressModalOpen}
          onClose={() => setIsAddressModalOpen(false)}
          onSave={handleAddressUpdate}
          customerAddresses={selectedCustomer?.address}
          receiverType={wizardData.receiverType}
          initialData={{
            description: wizardData.customerAddress,
            country: wizardData.customerCountry,
            countryCode: wizardData.customerForeignCountry,
            province: wizardData.customerProvince,
            district: wizardData.customerDistrict,
            township: wizardData.customerTownship,
            city: wizardData.customerCity,
            fiscalCode: wizardData.customerAddressFiscalCode,
          }}
        />
      )}

      {isBranchOfficeModalOpen && (
        <BranchOfficeFormModal
          isOpen={isBranchOfficeModalOpen}
          onClose={() => setIsBranchOfficeModalOpen(false)}
          onBranchOfficeCreated={(branchOffice) => {
            // Auto-select the newly created branch office
            onUpdate({ branchOfficeId: branchOffice.BranchOfficeId });
            setIsBranchOfficeModalOpen(false);
          }}
        />
      )}
    </div>
  );
}
